import React, {useRef, useEffect, useState, useCallback} from "react";
import {Trash2, Edit, Loader, Percent} from "lucide-react";
import {api} from "../../config/api";
import secureStorage from "../../utils/secureStorage";
import {useInfiniteQuery, useMutation, useQueryClient} from '@tanstack/react-query';
import ConfirmationDialog from "../Common/ConfirmationDialog";
import {SettingsToolbar, SelectionBar, SettingsTable, StatusBadge, AddAction, ListLoader} from "./SettingsUI";

const TaxesList = ({
                       searchTerm,
                       setSearchTerm,
                       debouncedSearchTerm,
                       selectedItems,
                       setSelectedItems,
                       selectedAll,
                       setSelectedAll,
                       showNotification,
                       onCreateTax,
                       onEditTax
                   }) => {
    const queryClient = useQueryClient();
    const loadingRef = useRef(null);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [pendingDelete, setPendingDelete] = useState(null);


    // Function to fetch taxes from the API with pagination and filtering
    const fetchTaxes = async ({pageParam = 1}) => {
        try {
            const accountId = await secureStorage.getItem("selectedAccount");

            // Build the query string with pagination and name filter
            let url = `/taxes?page=${pageParam}`;
            if (debouncedSearchTerm.trim()) {
                url += `&name=${encodeURIComponent(debouncedSearchTerm.trim())}`;
            }

            const response = await api.get(url, {
                headers: {
                    'X-account-id': accountId.id,
                    'Accept': 'application/json'
                }
            });

            return {
                data: response.data.items || [],
                currentPage: response.data.currentPage,
                totalPages: response.data.totalPages,
                hasNextPage: response.data.currentPage < response.data.totalPages
            };
        } catch (error) {
            const errorMessage = error.response?.data?.detail ||
                error.response?.data?.message ||
                "Une erreur lors de la récupération des taxes";
            throw new Error(errorMessage);
        }
    };

    // Function to delete taxes
    const deleteTaxes = async (taxIds) => {
        try {
            const accountId = await secureStorage.getItem("selectedAccount");

            // Delete taxes one by one
            const results = await Promise.all(
                taxIds.map(id =>
                    api.delete(`/taxes/${id}`, {
                        headers: {
                            'X-account-id': accountId.id,
                            'Accept': 'application/json'
                        }
                    }).catch(err => {
                        return {error: err, id};
                    })
                )
            );

            // Check for errors
            const failures = results.filter(r => r && r.error);
            if (failures.length > 0) {
                if (failures.length === taxIds.length) {
                    throw new Error("Impossible de supprimer les taxes sélectionnées");
                } else {
                    throw new Error(`${failures.length} sur ${taxIds.length} taxes n'ont pas pu être supprimées`);
                }
            }

            return taxIds;
        } catch (error) {
            const errorMessage = error.response?.data?.detail ||
                error.response?.data?.message ||
                error.message ||
                "Une erreur lors de la suppression des taxes";
            throw new Error(errorMessage);
        }
    };

    // Use react-query for delete mutation
    const deleteMutation = useMutation({
        mutationFn: deleteTaxes,
        onMutate: () => {
            showNotification('loading', 'Suppression en cours...');
        },
        onSuccess: () => {
            // Reset selection and refetch data
            setSelectedItems([]);
            setSelectedAll(false);
            queryClient.invalidateQueries({queryKey: ['taxes']});
            showNotification('success', 'Taxes supprimées avec succès');
        },
        onError: (error) => {
            showNotification('error', `Erreur: ${error.message}`);
        }
    });

    // Use react-query to fetch and cache the data with infinite query
    const {
        data,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        isLoading,
        error
    } = useInfiniteQuery({
        queryKey: ['taxes', debouncedSearchTerm],
        queryFn: fetchTaxes,
        getNextPageParam: (lastPage) => {
            return lastPage.hasNextPage ? lastPage.currentPage + 1 : undefined;
        },
    });

    // Flatten the pages data into a single taxes array
    const taxes = data?.pages.flatMap(page => page.data) || [];

    // Function to load more data
    const loadMore = useCallback(() => {
        if (hasNextPage && !isFetchingNextPage) {
            setIsLoadingMore(true);
            fetchNextPage().finally(() => setIsLoadingMore(false));
        }
    }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

    // Implement Intersection Observer for infinite scrolling
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    loadMore();
                }
            },
            {threshold: 0.5}
        );

        if (loadingRef.current) {
            observer.observe(loadingRef.current);
        }

        return () => {
            if (loadingRef.current) {
                observer.unobserve(loadingRef.current);
            }
        };
    }, [loadMore]);

    // Reset when search term changes
    useEffect(() => {
        setSelectedItems([]);
        setSelectedAll(false);
    }, [debouncedSearchTerm, setSelectedItems, setSelectedAll]);

    // Handle select all checkbox
    const handleSelectAll = () => {
        const newSelectedAll = !selectedAll;
        setSelectedAll(newSelectedAll);
        setSelectedItems(newSelectedAll ? taxes.map(item => item.id) : []);
    };

    // Handle individual item selection
    const toggleItem = (taxId) => {
        setSelectedItems((prevSelected) =>
            prevSelected.includes(taxId)
                ? prevSelected.filter((id) => id !== taxId)
                : [...prevSelected, taxId]
        );
    };

    // Format percentage for display
    const formatPercentage = (value) => {
        return `${value}%`;
    };

    // ids awaiting confirmation: the selection, or a single row from its menu
    const askDelete = (ids) => setPendingDelete(ids);

    const confirmDelete = () => {
        deleteMutation.mutate(pendingDelete);
        setPendingDelete(null);
    };

    const rowActions = (tax) => [
        {label: 'Modifier', icon: Edit, onClick: () => onEditTax(tax), tone: 'blue'},
        {label: 'Supprimer', icon: Trash2, onClick: () => askDelete([tax.id]), tone: 'red', danger: true},
    ];

    const columns = [
        {key: 'name', label: 'Nom', render: (tax) => tax.name},
        {key: 'value', label: 'Taux', render: (tax) => <span className="tabular-nums">{formatPercentage(tax.value)}</span>},
        {key: 'active', label: 'Statut', render: (tax) => <StatusBadge active={tax.active}/>},
    ];

    return (
        <div>
            <AddAction onClick={onCreateTax}/>
            <SettingsToolbar search={searchTerm} onSearch={setSearchTerm} placeholder="Rechercher une taxe..."/>

            <SelectionBar count={selectedItems.length} singular="taxe sélectionnée" plural="taxes sélectionnées"
                          onDelete={() => askDelete(selectedItems)} deleting={deleteMutation.isPending}/>

            {isLoading && !data ? (
                <ListLoader label="Chargement des taxes..."/>
            ) : error && !data ? (
                <div className="p-4 text-center text-red-500">Erreur : {error.message}</div>
            ) : (
                <SettingsTable
                    columns={columns}
                    rows={taxes}
                    selection={{selected: selectedItems, onToggle: toggleItem, allSelected: selectedAll, onToggleAll: handleSelectAll}}
                    rowActions={rowActions}
                    onRowClick={onEditTax}
                    empty={{
                        icon: Percent,
                        title: debouncedSearchTerm ? 'Aucune taxe trouvée' : 'Aucune taxe',
                        text: debouncedSearchTerm ? 'Ajustez la recherche pour retrouver vos taxes.' : 'Créez une taxe pour l\'appliquer à vos produits.',
                    }}
                >
                    {(hasNextPage || isFetchingNextPage) && (
                        <div ref={loadingRef} className="flex justify-center items-center py-4">
                            <Loader className="w-6 h-6 text-primary animate-spin"/>
                        </div>
                    )}
                </SettingsTable>
            )}

            {pendingDelete && (
                <ConfirmationDialog
                    title="Confirmation de suppression"
                    message={`Êtes-vous sûr de vouloir supprimer ${pendingDelete.length} taxe${pendingDelete.length > 1 ? 's' : ''} ? Cette action est irréversible.`}
                    confirmLabel="Confirmer"
                    cancelLabel="Annuler"
                    onConfirm={confirmDelete}
                    onCancel={() => setPendingDelete(null)}
                    danger={true}
                />
            )}
        </div>
    );
};

export default TaxesList;
